/**
 * Third-party catalogue metadata, behind one seam.
 *
 * The four adapters — TMDB, Open Library, Discogs, and the AI fallback woven through each — sit in
 * `providers/`, and what crosses the seam is one normalised `EnrichedMediaResult`. ADR 0009 names
 * this as one of the four seams that are real today by the two-adapter test.
 *
 * **The seam survived deleting the HTTP hop, which is how you know the transport was never the
 * seam.** Until #407 the providers lived inside three Next.js route modules and this file reached
 * them with a cookie-less `fetch` at the app's own origin; every call hit the routes'
 * `getUser()` guard and came back 401, so enrichment silently returned nothing in production
 * (#78). The functions moved out of the routes, this file calls them directly, and `getBaseUrl`
 * lost its last caller and went.
 */

import { trackError, isDevelopment } from '@/lib/errors'

import { lookupMovie } from './providers/tmdb'
import { lookupBook } from './providers/openlibrary'
import { hasDiscogsCredentials, lookupMusic } from './providers/discogs'

export type MediaType = 'movie' | 'book' | 'artist' | 'label' | 'release'

/** AI-provided fallback info from analyze-entry */
export interface AIFallbackInfo {
  summary?: string
  genres?: string[]
}

export interface BaseEnrichRequest {
  type: MediaType
  name: string // The detected name from AI analysis
}

export interface MovieEnrichRequest extends BaseEnrichRequest {
  type: 'movie'
  year?: number
}

export interface BookEnrichRequest extends BaseEnrichRequest {
  type: 'book'
  author?: string
  /** AI-provided summary from analyze-entry (used as fallback) */
  aiFallback?: AIFallbackInfo
}

export interface ArtistEnrichRequest extends BaseEnrichRequest {
  type: 'artist'
  /** True if user explicitly said "artist" */
  explicitType?: boolean
  /** AI-provided summary/genres from analyze-entry (used as fallback) */
  aiFallback?: AIFallbackInfo
}

export interface LabelEnrichRequest extends BaseEnrichRequest {
  type: 'label'
  /** True if user explicitly said "label" */
  explicitType?: boolean
  /** AI-provided summary/genres from analyze-entry (used as fallback) */
  aiFallback?: AIFallbackInfo
}

export interface ReleaseEnrichRequest extends BaseEnrichRequest {
  type: 'release'
  artistName?: string
  /** True if user explicitly said "release" or "album" */
  explicitType?: boolean
}

export type EnrichRequest =
  | MovieEnrichRequest
  | BookEnrichRequest
  | ArtistEnrichRequest
  | LabelEnrichRequest
  | ReleaseEnrichRequest

/** Label info for linking to artist */
export interface LabelInfo {
  externalId: string
  name: string
}

/**
 * One other match the catalogue returned, for the user to disambiguate against.
 *
 * Named on #397 while the twelve importers of the route hop were being repointed here.
 * `components/library/media-preview/types.ts:10-18` declared the same six fields, same
 * optionality, under this name; #405 deletes that copy and imports this one.
 */
export interface AlternativeResult {
  externalId: string
  name: string
  imageUrl?: string
  year?: number
  type?: string // For mixed results
  genres?: string[] // For display in alternatives
}

export interface EnrichedMediaResult {
  type: MediaType
  // AI-provided data (raw input)
  name: string
  // Enriched data from external API
  enriched: {
    externalId?: string
    title?: string // Properly formatted title from external API
    imageUrl?: string
    description?: string
    // Type-specific enriched fields
    director?: string // movies
    author?: string // books
    genres?: string[]
    topics?: string[] // books
    year?: number
    labels?: string[] // artists - label names for display
    labelInfo?: LabelInfo[] // artists - full label info for storing
    website?: string // labels
    artistNames?: string[] // labels - notable artists on the label
    artistName?: string // tracks
    labelName?: string // tracks
  } | null
  // Alternative matches for disambiguation
  alternatives: AlternativeResult[]
  // Confidence in the match (based on whether we found results)
  confidence: 'high' | 'medium' | 'low' | 'none'
}

export async function enrichMovie(
  name: string,
  year?: number,
): Promise<EnrichedMediaResult> {
  try {
    // In process, not over HTTP. Until #407 this opened a cookie-less `fetch` at the app's own
    // `/api/lookup/tmdb`, whose `getUser()` guard returned 401 every time (#78). The
    // response-not-ok branch that stood here is gone with the request: a provider that fails now
    // throws, and the catch below already returns the same unenriched result.
    const data = await lookupMovie(name, year)

    if (!data.bestMatch) {
      return {
        type: 'movie',
        name,
        enriched: null,
        alternatives: [],
        confidence: 'none',
      }
    }

    return {
      type: 'movie',
      name,
      enriched: {
        externalId: data.bestMatch.externalId,
        title: data.bestMatch.title, // Properly formatted title from TMDB
        imageUrl: data.bestMatch.posterUrl,
        description: data.bestMatch.overview,
        director: data.bestMatch.director,
        genres: data.bestMatch.genres,
        year: data.bestMatch.year,
      },
      // The annotation that stood here was `Record<string, unknown>`, which is what
      // `response.json()` gave. The in-process call is typed, so inference is better than the
      // annotation — one of the things #78's HTTP hop was costing.
      alternatives: (data.alternatives || []).map((a) => ({
        externalId: a.externalId,
        name: a.title,
        imageUrl: a.posterUrl,
        year: a.year,
      })),
      confidence: data.bestMatch.externalId ? 'high' : 'low',
    }
  } catch (error) {
    trackError(error, { route: '/api/enrich-media', mediaType: 'movie', name })
    return {
      type: 'movie',
      name,
      enriched: null,
      alternatives: [],
      confidence: 'none',
    }
  }
}

export async function enrichBook(
  name: string,
  author?: string,
  aiFallback?: AIFallbackInfo,
): Promise<EnrichedMediaResult> {
  if (isDevelopment) console.log('[enrichBook] Looking up:', { name, author })

  try {
    // In process, not over HTTP (#78, #407). The response-not-ok branch that stood here returned
    // exactly what the catch below returns — the AI summary when there is one, an unenriched
    // result otherwise — so a throwing provider is covered without repeating it.
    const data = await lookupBook(name, author)
    if (isDevelopment) {
      console.log('[enrichBook] OpenLibrary response:', {
        hasMatch: !!data.bestMatch,
        topics: data.bestMatch?.topics,
        topicsCount: data.bestMatch?.topics?.length,
      })
    }

    if (!data.bestMatch) {
      // If no match but we have AI fallback, use it
      if (aiFallback?.summary) {
        return {
          type: 'book',
          name,
          enriched: {
            title: name,
            author,
            description: aiFallback.summary,
          },
          alternatives: [],
          confidence: 'low',
        }
      }
      return {
        type: 'book',
        name,
        enriched: null,
        alternatives: [],
        confidence: 'none',
      }
    }

    // Use OpenLibrary data, falling back to AI summary if description missing
    const description = data.bestMatch.description || aiFallback?.summary
    const topics = data.bestMatch.topics || []

    if (isDevelopment) {
      console.log('[enrichBook] Final enriched data:', {
        title: data.bestMatch.title,
        hasDescription: !!description,
        topics,
        topicsCount: topics.length,
      })
    }

    return {
      type: 'book',
      name,
      enriched: {
        externalId: data.bestMatch.externalId,
        title: data.bestMatch.title, // Properly formatted title from OpenLibrary
        imageUrl: data.bestMatch.coverUrl,
        description,
        author: data.bestMatch.author,
        topics,
        year: data.bestMatch.year,
      },
      // The annotation that stood here was `Record<string, unknown>`, which is what
      // `response.json()` gave. The in-process call is typed, so inference is better than the
      // annotation — one of the things #78's HTTP hop was costing.
      alternatives: (data.alternatives || []).map((a) => ({
        externalId: a.externalId,
        name: a.title,
        imageUrl: a.coverUrl,
        year: a.year,
      })),
      confidence: data.bestMatch.externalId ? 'high' : 'low',
    }
  } catch (error) {
    trackError(error, { route: '/api/enrich-media', mediaType: 'book', name })
    // If fetch fails but we have AI fallback, use it
    if (aiFallback?.summary) {
      return {
        type: 'book',
        name,
        enriched: {
          title: name,
          author,
          description: aiFallback.summary,
        },
        alternatives: [],
        confidence: 'low',
      }
    }
    return {
      type: 'book',
      name,
      enriched: null,
      alternatives: [],
      confidence: 'none',
    }
  }
}

/**
 * Enrich music by searching Discogs across all types (artist, label, release).
 * The Discogs API returns the best match with its detected type.
 * Uses AI-provided fallback info from analyze-entry when Discogs data is missing.
 *
 * @param typeHint - Optional type hint from AI detection (used for prioritization)
 * @param name - The name/title to search for
 * @param artistName - Optional artist name for artist/label searches
 * @param explicitType - If true, filter to only this type (user explicitly specified)
 * @param aiFallback - AI-provided summary/genres from analyze-entry (used as fallback)
 */
export async function enrichMusic(
  typeHint: 'artist' | 'label' | 'release',
  name: string,
  artistName?: string,
  explicitType?: boolean,
  aiFallback?: AIFallbackInfo,
): Promise<EnrichedMediaResult> {
  // Build optimized query URL with type-specific parameters
  // The Discogs lookup API will use type-specific parameters (artist, label, release_title, title)
  // instead of generic 'q' parameter for better results

  if (isDevelopment) {
    console.log('[enrichMusic] Looking up:', {
      typeHint,
      name,
      artistName,
      explicitType,
    })
  }

  // Check if we have AI fallback for artist/label types
  const hasAIFallback =
    (typeHint === 'artist' || typeHint === 'label') &&
    (aiFallback?.summary ||
      (aiFallback?.genres && aiFallback.genres.length > 0))

  // `lookupMusic` states that credentials are present as its precondition, and until #407 only the
  // route checked it — this file reached the provider through that route's 503. Calling in process
  // makes this the other caller of the same precondition: without the check an unset
  // `DISCOGS_API_KEY` costs a doomed unauthenticated round trip and a `trackError` per capture
  // before landing on the same AI fallback.
  if (!hasDiscogsCredentials()) {
    if (hasAIFallback) {
      return {
        type: typeHint,
        name,
        enriched: {
          title: name,
          description: aiFallback?.summary,
          genres: aiFallback?.genres,
        },
        alternatives: [],
        confidence: 'low',
      }
    }
    return {
      type: typeHint,
      name,
      enriched: null,
      alternatives: [],
      confidence: 'none',
    }
  }

  try {
    // In process, not over HTTP (#78, #407). The response-not-ok branch that stood here — which
    // also caught the route's 503 for missing Discogs credentials — returned what the catch below
    // returns: the AI summary when there is one, an unenriched result otherwise. A provider that
    // cannot authenticate now throws into the same place.
    const data = await lookupMusic(name, {
      typeHint,
      artistName,
      isExplicitType: explicitType,
    })
    if (isDevelopment) {
      console.log('[enrichMusic] Discogs response:', {
        hasMatch: !!data.bestMatch,
        matchType: data.bestMatch?.type,
        error: 'error' in data ? data.error : undefined,
        alternativesCount: data.alternatives?.length,
      })
    }

    if (!data.bestMatch) {
      // If Discogs has no match but we have AI fallback, use it
      if (hasAIFallback) {
        return {
          type: typeHint,
          name,
          enriched: {
            title: name,
            description: aiFallback?.summary,
            genres: aiFallback?.genres,
          },
          alternatives: [],
          confidence: 'low',
        }
      }
      return {
        type: typeHint as MediaType,
        name,
        enriched: null,
        alternatives: [],
        confidence: 'none',
      }
    }

    // Use the type from Discogs result (may differ from typeHint)
    const foundType = data.bestMatch.type as 'artist' | 'label' | 'release'

    // Build enriched data based on the found type
    const enriched: EnrichedMediaResult['enriched'] = {
      externalId: data.bestMatch.externalId,
      title: data.bestMatch.name,
      imageUrl: data.bestMatch.imageUrl,
    }

    // Add type-specific fields
    if (foundType === 'artist') {
      const discogsDescription = data.bestMatch.bio
      const discogsGenres = data.bestMatch.genres

      // Use Discogs data, falling back to AI if missing
      enriched.description =
        discogsDescription || aiFallback?.summary || undefined
      enriched.genres =
        discogsGenres && discogsGenres.length > 0
          ? discogsGenres
          : aiFallback?.genres || undefined
      enriched.labels = data.bestMatch.labelNames // Label names for display
      enriched.labelInfo = data.bestMatch.labelInfo // Full label info for storing
    } else if (foundType === 'label') {
      const discogsDescription = data.bestMatch.profile
      const discogsGenres = data.bestMatch.genres

      // Use Discogs data, falling back to AI if missing
      enriched.description =
        discogsDescription || aiFallback?.summary || undefined
      enriched.genres =
        discogsGenres && discogsGenres.length > 0
          ? discogsGenres
          : aiFallback?.genres || undefined
      enriched.website = data.bestMatch.website
      enriched.artistNames = data.bestMatch.artistNames // Notable artists on the label
    } else if (foundType === 'release') {
      enriched.genres = data.bestMatch.genres
      enriched.year = data.bestMatch.year
      enriched.artistName = data.bestMatch.artistName
      enriched.labelName = data.bestMatch.labelName
    }

    return {
      type: foundType, // Use the actual type found by Discogs
      name,
      enriched,
      // The annotation that stood here was `Record<string, unknown>`, which is what
      // `response.json()` gave. The in-process call is typed, so inference is better than the
      // annotation — one of the things #78's HTTP hop was costing.
      alternatives: (data.alternatives || []).map((a) => ({
        externalId: a.externalId as string,
        name: a.name as string,
        imageUrl: a.imageUrl as string | undefined,
        type: a.type as string | undefined, // Include type in alternatives for UI
        genres: a.genres as string[] | undefined, // Include genres for display
        year: a.year as number | undefined,
      })),
      confidence: data.bestMatch.externalId ? 'high' : 'low',
    }
  } catch (error) {
    trackError(error, { route: '/api/enrich-media', mediaType: typeHint, name })
    // If fetch fails but we have AI fallback, use it
    if (hasAIFallback) {
      return {
        type: typeHint,
        name,
        enriched: {
          title: name,
          description: aiFallback?.summary,
          genres: aiFallback?.genres,
        },
        alternatives: [],
        confidence: 'low',
      }
    }
    return {
      type: typeHint as MediaType,
      name,
      enriched: null,
      alternatives: [],
      confidence: 'none',
    }
  }
}

/**
 * Enrich a media entry based on its analysis result.
 * Called from both the /api/enrich-media route and the capture pipeline.
 */
export async function enrichFromAnalysis(
  type: MediaType,
  name: string,
  opts?: {
    year?: number
    author?: string
    artistName?: string
    explicitType?: boolean
    aiFallback?: AIFallbackInfo
  },
): Promise<EnrichedMediaResult> {
  switch (type) {
    case 'movie':
      return enrichMovie(name, opts?.year)
    case 'book':
      return enrichBook(name, opts?.author, opts?.aiFallback)
    case 'artist':
    case 'label':
    case 'release':
      return enrichMusic(
        type,
        name,
        opts?.artistName,
        opts?.explicitType,
        opts?.aiFallback,
      )
    default: {
      const _exhaustive: never = type
      throw new Error(`Unknown media type: ${_exhaustive}`)
    }
  }
}

/**
 * The capture pipeline's entry point.
 *
 * It resolved a base URL and called itself over HTTP until #407. There is nothing to resolve now:
 * the providers are modules, and this runs wherever the enricher runs.
 */
export async function enrichMedia(
  type: string,
  title: string,
): Promise<EnrichedMediaResult> {
  return enrichFromAnalysis(type as MediaType, title)
}
